<template>
    <div>
        <!-- Header -->
        <div class="mb-6">
            <back-button :route="{ name: 'audit' }" />

            <div class="mt-3 flex items-start justify-between">
                <div>
                    <div class="flex items-center gap-2.5">
                        <h1 class="text-xl font-semibold tracking-tight text-gray-900 dark:text-white">
                            Auditoria
                        </h1>
                        <span class="font-mono text-sm text-gray-400 dark:text-gray-500">#{{ audit.hash }}</span>
                        <audit-status v-if="audit" :audit="audit" />
                    </div>
                    <div class="mt-1 flex items-center gap-2 text-xs text-gray-500">
                        <ClockIcon class="h-3.5 w-3.5" />
                        <span>Iniciado em {{ audit.created_at }}</span>
                        <span v-if="audit.due_days_left" class="text-gray-400 dark:text-gray-600">·</span>
                        <span v-if="audit.due_days_left" class="text-amber-600 dark:text-amber-500/80">
                            {{ audit.due_days_left }}d restantes
                        </span>
                    </div>
                </div>

                <div class="flex items-center gap-2">
                    <submit-button
                        @click="syncClickUp(audit.id)"
                        :disabled="isSyncing(audit.id)"
                        :loading="isSyncing(audit.id)"
                        size="sm"
                    >
                        ClickUp
                    </submit-button>

                    <a
                        v-if="audit.clickup_id"
                        :href="`https://app.clickup.com/t/${audit.clickup_id}`"
                        target="_blank"
                        class="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                        Task
                        <ArrowTopRightOnSquareIcon class="h-3.5 w-3.5" aria-hidden="true" />
                    </a>

                    <audit-actions
                        v-if="audit"
                        :audit="audit"
                        @refund="refundAll = true"
                        @export-pdf="exportPdf"
                        @fetch="fetch"
                    />
                </div>
            </div>
        </div>

        <!-- User bar -->
        <user-heading :user="audit.user" v-if="audit" />

        <!-- Refund deadline alert -->
        <div v-if="audit && audit.refund_deadline" class="mb-4">
            <div class="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-500/20 dark:bg-amber-500/5">
                <ExclamationCircleIcon class="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
                <div>
                    <p class="text-sm font-medium text-amber-700 dark:text-amber-400">Prazo de reembolso</p>
                    <p class="mt-0.5 text-sm text-amber-600 dark:text-amber-500/70">
                        O reembolso das vendas deve ocorrer até {{ audit.refund_deadline }}.
                    </p>
                </div>
            </div>
        </div>

        <!-- Main grid -->
        <div class="grid grid-cols-4 gap-5">
            <!-- Content area -->
            <div class="col-span-3 space-y-4">
                <refund-transactions
                    v-if="audit && refundAll"
                    :audit="audit"
                />

                <!-- AI Analysis card -->
                <card>
                    <div class="flex flex-col dark:text-white">
                        <div class="mb-3 flex items-center gap-2 text-xs text-gray-500">
                            <div class="h-1.5 w-1.5 rounded-full bg-indigo-500"></div>
                            Análise IA
                        </div>

                        <div
                            v-if="audit.messages && audit.messages.length"
                            class="mb-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-700/50"
                        >
                            <p class="text-xs text-gray-500 dark:text-gray-400">
                                Conteúdo gerado por IA — revise e verifique as informações.
                            </p>
                        </div>

                        <markdown-renderer
                            class="mb-4 rounded-lg"
                            v-for="message in audit.messages"
                            :source="message.message"
                        />
                    </div>
                    <chat v-model="message" @generated="storeMessage" />
                </card>

                <!-- Tabs -->
                <nav-tabs
                    class="mb-3"
                    :exact="false"
                    v-if="audit"
                    :tabs="[
                        {
                            label: 'Interações',
                            route: 'audit.interactions',
                            params: { audit_id: audit.id },
                            icon: UsersIcon,
                        },
                        {
                            label: 'Métricas',
                            route: 'audit.metrics',
                            params: { audit_id: audit.id },
                            icon: UsersIcon,
                        },
                        {
                            label: 'Clicks',
                            route: 'audit.trackings',
                            params: { audit_id: audit.id },
                            icon: UsersIcon,
                        },
                        {
                            label: 'Produtos',
                            route: 'audit.products',
                            params: { audit_id: audit.id },
                            icon: CubeIcon,
                        },
                        {
                            label: 'Vendas',
                            route: 'audit.commissions',
                            params: { audit_id: audit.id },
                            icon: BanknotesIcon,
                        },
                        {
                            label: 'Logins',
                            route: 'audit.logins',
                            params: { audit_id: audit.id },
                            icon: UserIcon,
                        },
                        {
                            label: 'Financeiro',
                            route: 'audit.finnancial',
                            params: { audit_id: audit.id },
                            icon: CurrencyDollarIcon,
                        },
                    ]"
                />
                <router-view
                    v-if="audit"
                    :user="audit.user"
                    :audit="audit"
                    @fetch="fetch"
                />
            </div>

            <!-- Sidebar -->
            <div>
                <audit-interactions
                    :audit="audit"
                    @fetch="fetch"
                    :show-card="true"
                    :show-interactions="false"
                />
            </div>
        </div>
    </div>
</template>

<script>
import Chat from "../../components/ai/Chat.vue";
import MarkdownIt from "markdown-it";
import BackButton from "../../components/layouts/navigation/BackButton.vue";
import UserHeading from "../users/UserHeading.vue";
import Card from "../../components/layouts/Card.vue";
import InfoCol from "../../components/layouts/InfoCol.vue";
import BooleanIcon from "../../components/layouts/BooleanIcon.vue";
import TimelineSkeleton from "../../components/layouts/skeleton/TimelineSkeleton.vue";
import AuditInteractions from "../../components/audit/AuditInteractions.vue";
import AuditStatus from "../../components/audit/AuditStatus.vue";
import {
    UsersIcon,
    CubeIcon,
    BanknotesIcon,
    UserIcon,
    CurrencyDollarIcon,
    ExclamationCircleIcon,
    ClockIcon,
} from "@heroicons/vue/24/outline";
import NavTabs from "../../components/layouts/NavTabs.vue";
import Navigation from "../../components/layouts/navigation/vertical/Navigation.vue";
import SubmitButton from "../../components/forms/SubmitButton.vue";
import RefundTransactions from "../../components/audit/RefundTransactions.vue";
import { mapWritableState } from "pinia";
import { Store } from "../../stores/store";
import MarkdownRenderer from "../../components/layouts/MarkdownRenderer.vue";
import ScoreGauge from "../../components/user/score/ScoreGauge.vue";
import Score from "../../components/user/Score.vue";
import AuditActions from "./AuditActions.vue";
import { useToast } from "vue-toastification";
import { ArrowTopRightOnSquareIcon } from "@heroicons/vue/24/outline";
import { ref, onMounted } from 'vue';

export default {
    components: {
        Chat,
        AuditActions,
        AuditStatus,
        Score,
        ScoreGauge,
        MarkdownRenderer,
        RefundTransactions,
        SubmitButton,
        ExclamationCircleIcon,
        NavTabs,
        Navigation,
        AuditInteractions,
        TimelineSkeleton,
        BooleanIcon,
        InfoCol,
        Card,
        UserHeading,
        BackButton,
        ClockIcon,
        ArrowTopRightOnSquareIcon,
    },
    data() {
        return {
            refundAll: false,
            generating: false,
            message: "",
            loading: false,
            audit: false,
            syncing: false,
            approve: false,
        };
    },

    computed: {
        ...mapWritableState(Store, ["authUser"]),
        setting() {
            if (!this.audit) {
                return false;
            }
            return this.audit.user.setting;
        },
    },

    setup(props) {
        const markdown = new MarkdownIt();
        const toast = useToast();
        const syncStates = ref({});

        const isSyncing = (auditId) => {
            return ['syncing', 'locked'].includes(syncStates.value[auditId]);
        };

        const syncButtonText = (auditId) => {
            const status = syncStates.value[auditId];
            switch (status) {
                case 'syncing': return 'Sincronizando...';
                case 'locked': return 'Já está sincronizando...';
                case 'failed': return 'Tentar novamente';
                default: return 'Sincronizar ClickUp';
            }
        };

        const syncClickUp = async (auditId) => {
            try {
                syncStates.value[auditId] = 'syncing';
                await axios.post(`/api/audit/${auditId}/clickup`);
            } catch (error) {
                syncStates.value[auditId] = 'failed';
                throw error;
            }
        };

        return {
            syncStates,
            isSyncing,
            syncButtonText,
            syncClickUp
        };
    },

    mounted() {
        this.fetch();

        Echo.channel("audit." + this.$route.params.audit_id)
            .listen(".App\\Events\\Audit\\GeneratingDescription", (e) => {
                this.generating = e.generating;
                if (!e.generating) {
                    this.fetch();
                }
            })
            .listen('.App\\Events\\ClickUpSyncStatusEvent', (e) => {
                this.syncStates[e.audit_id] = e.status;

                if (e.status === 'completed') {
                    this.fetch();
                }
            });
    },
    methods: {
        exportPdf() {
            window.open("/api/audit/" + this.$route.params.audit_id + "/pdf");
        },
        storeMessage() {
            axios
                .post(
                    "/api/audit/" + this.$route.params.audit_id + "/messages",
                    {
                        message: this.message,
                    },
                )
        },
        generateReport() {
            this.generating = true;
            axios
                .get("/api/audit/" + this.$route.params.audit_id + "/report")
                .then(({ data }) => {});
        },
        fetch() {
            this.loading = true;

            axios
                .get("/api/audit/" + this.$route.params.audit_id)
                .then(({ data }) => {
                    this.audit = data;
                    this.loading = false;
                });
        },
        async syncWithClickUp() {
            try {
                this.syncing = true;
                await axios.post(`/api/audit/${this.$route.params.audit_id}/clickup`);
                this.toast.success("Task sincronizada com sucesso!");
            } catch (error) {
                console.error(error);
                this.toast.error("Erro ao sincronizar com ClickUp");
            } finally {
                this.syncing = false;
            }
        },
        UsersIcon,
        CubeIcon,
        BanknotesIcon,
        UserIcon,
        CurrencyDollarIcon,
    },
};
</script>
